import React from 'react';
import { ImagePlaceholderProps } from '../../../types/DesignSystem/ImagePlaceholder';
import Typography from '../Typography/Typography';
import { ImageIcon } from '../Iconography/Icons';
import { withClassName } from '../cn';

/**
 * ImagePlaceholder (§ 191) — the ground that stands where an image should be.
 *
 * Extracted from the category detail/edit views, which were rendering
 * Wikipedia's `No-Image-Placeholder.svg` **from Wikipedia** — an external
 * network fetch on every categoryless render, drawn in grey literals that can
 * never theme. (One more consumer of that URL remains in
 * `Mapping/CreateCategoryModal.tsx`, left for its module's own pass.)
 *
 * The vocabulary is deliberately `Skeleton`'s neighbourhood, not `EmptyState`'s:
 * a skeleton is content that has not arrived YET, an empty state is a page with
 * nothing to show — this is a single media slot whose value is simply absent.
 * It keeps the slot's geometry (width/height from the caller, like `Skeleton`),
 * grounds on `surface-sunken` behind an `edge` rule, and inks the glyph and
 * caption in `ink-tertiary` — quiet by design; an absent thumbnail is metadata,
 * not a call to action.
 *
 * `role="img"` + `aria-label`: the region is announced as the image it stands
 * for ("No image available"), instead of being a mute decorated div — which is
 * also exactly what the `<img alt>` it replaces used to contribute.
 */
const ImagePlaceholder = React.forwardRef<HTMLDivElement, ImagePlaceholderProps>(
  function ImagePlaceholder({
    label = 'No image available',
    showLabel = true,
    width,
    height,
    className = '',
    'data-testid': dataTestId,
    style,
    ...rest
  }, ref) {
    return (
      <div
        {...rest}
        ref={ref}
        role="img"
        aria-label={label}
        data-testid={dataTestId}
        className={withClassName(
          'tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 '
          + 'tw-bg-surface-sunken tw-border tw-border-edge tw-rounded-ctl '
          + 'tw-text-ink-tertiary tw-p-3 ',
          className,
        )}
        style={{ width: width ?? '100%', height, ...style }}
      >
        <ImageIcon size={28} aria-hidden="true" />
        {showLabel && (
          <Typography
            variant="caption-metadata"
            as="span"
            className="tw-text-ink-tertiary tw-text-center"
          >
            {label}
          </Typography>
        )}
      </div>
    );
  }
);

export default ImagePlaceholder;
